import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Expertises from "@/pages/Expertises";
import Approche from "@/pages/Approche";
import Collectivites from "@/pages/Collectivites";
import Ecosysteme from "@/pages/Ecosysteme";
import MentionsLegales from "@/pages/MentionsLegales";
import Confidentialite from "@/pages/Confidentialite";
import CGU from "@/pages/CGU";
import EspacePrive from "@/pages/EspacePrive";
import Articles from "@/pages/Articles";
import ArticleDetail from "@/pages/ArticleDetail";
import Contact from "@/pages/Contact";
import AdminLogin from "@/pages/admin/Login";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminArticleEditor from "@/pages/admin/ArticleEditor";
import AdminContentEditor from "@/pages/admin/ContentEditor";
import CockpitLayout from "@/pages/cockpit/CockpitLayout";
import CockpitLogin from "@/pages/cockpit/CockpitLogin";
import CockpitDashboard from "@/pages/cockpit/CockpitDashboard";
import CockpitDossiers from "@/pages/cockpit/CockpitDossiers";
import CockpitDossierDetail from "@/pages/cockpit/CockpitDossierDetail";
import CockpitPartners from "@/pages/cockpit/CockpitPartners";
import CockpitProspects from "@/pages/cockpit/CockpitProspects";
import CockpitFinance from "@/pages/cockpit/CockpitFinance";
import CockpitAdmin from "@/pages/cockpit/CockpitAdmin";
import CockpitUsers from "@/pages/cockpit/CockpitUsers";
import CockpitSettings from "@/pages/cockpit/CockpitSettings";
import ClientLogin from "@/pages/client/ClientLogin";
import ClientEspace from "@/pages/client/ClientEspace";
import { AuthProvider, useAuth } from "@/lib/auth";
import { CockpitAuthProvider, useCockpitAuth } from "@/lib/cockpitAuth";
import { ClientAuthProvider } from "@/lib/clientAuth";
import { I18nProvider } from "@/lib/i18n";

function ProtectedAdmin({ children }) {
    const { user, ready } = useAuth();
    if (!ready) return <div className="min-h-screen bg-xv-ivory" />;
    if (!user) return <Navigate to="/admin/login" replace />;
    return children;
}

function ProtectedCockpit({ children }) {
    const { user, ready } = useCockpitAuth();
    if (!ready) return <div className="min-h-screen bg-[#070d18]" />;
    if (!user) return <Navigate to="/cockpit/login" replace />;
    return children;
}

function SuperOnly({ children }) {
    const { user, ready } = useCockpitAuth();
    if (!ready) return <div className="min-h-screen bg-[#070d18]" />;
    if (!user) return <Navigate to="/cockpit/login" replace />;
    if (user.role !== "super_admin") return <Navigate to="/cockpit" replace />;
    return children;
}

function PublicLayout({ children }) {
    return (
        <>
            <Navbar />
            <main>{children}</main>
            <Footer />
        </>
    );
}

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <CockpitAuthProvider>
                    <ClientAuthProvider>
                        <I18nProvider>
                        <BrowserRouter>
                            <ScrollToTop />
                    <Routes>
                        {/* Public */}
                        <Route
                            path="/"
                            element={
                                <PublicLayout>
                                    <Home />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/a-propos"
                            element={
                                <PublicLayout>
                                    <About />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/expertises"
                            element={
                                <PublicLayout>
                                    <Expertises />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/approche"
                            element={
                                <PublicLayout>
                                    <Approche />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/ecosysteme"
                            element={
                                <PublicLayout>
                                    <Ecosysteme />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/mentions-legales"
                            element={
                                <PublicLayout>
                                    <MentionsLegales />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/confidentialite"
                            element={
                                <PublicLayout>
                                    <Confidentialite />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/cgu"
                            element={
                                <PublicLayout>
                                    <CGU />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/collectivites"
                            element={
                                <PublicLayout>
                                    <Collectivites />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/articles"
                            element={
                                <PublicLayout>
                                    <Articles />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/articles/:slug"
                            element={
                                <PublicLayout>
                                    <ArticleDetail />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/contact"
                            element={
                                <PublicLayout>
                                    <Contact />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/espace-prive"
                            element={
                                <PublicLayout>
                                    <EspacePrive />
                                </PublicLayout>
                            }
                        />

                        {/* Admin */}
                        <Route path="/admin/login" element={<AdminLogin />} />
                        <Route
                            path="/admin"
                            element={
                                <ProtectedAdmin>
                                    <AdminDashboard />
                                </ProtectedAdmin>
                            }
                        />
                        <Route
                            path="/admin/articles/new"
                            element={
                                <ProtectedAdmin>
                                    <AdminArticleEditor />
                                </ProtectedAdmin>
                            }
                        />
                        <Route
                            path="/admin/articles/:id"
                            element={
                                <ProtectedAdmin>
                                    <AdminArticleEditor />
                                </ProtectedAdmin>
                            }
                        />
                        <Route
                            path="/admin/content/:key"
                            element={
                                <ProtectedAdmin>
                                    <AdminContentEditor />
                                </ProtectedAdmin>
                            }
                        />

                        {/* Cockpit — strategic operations */}
                        <Route path="/cockpit/login" element={<CockpitLogin />} />
                        <Route
                            path="/cockpit"
                            element={
                                <ProtectedCockpit>
                                    <CockpitLayout />
                                </ProtectedCockpit>
                            }
                        >
                            <Route index element={<CockpitDashboard />} />
                            <Route path="dossiers" element={<CockpitDossiers />} />
                            <Route path="dossiers/:id" element={<CockpitDossierDetail />} />
                            <Route path="partenaires" element={<CockpitPartners />} />
                            <Route path="prospection" element={<CockpitProspects />} />
                            <Route path="finance" element={<CockpitFinance />} />
                            <Route path="administratif" element={<CockpitAdmin />} />
                            <Route path="utilisateurs" element={<SuperOnly><CockpitUsers /></SuperOnly>} />
                            <Route path="parametres" element={<CockpitSettings />} />
                        </Route>

                        {/* Client portal */}
                        <Route path="/client/login" element={<ClientLogin />} />
                        <Route path="/client/espace" element={<ClientEspace />} />
                    </Routes>
                    </BrowserRouter>
                        </I18nProvider>
                    </ClientAuthProvider>
                </CockpitAuthProvider>
            </AuthProvider>
        </div>
    );
}

export default App;
